import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Chip,
  IconButton,
  Button,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Stack,
  Alert,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  Archive as ArchiveIcon,
  Warning as WarningIcon,
  Star as OwnerIcon,
  CloudUpload as CloudUploadIcon,
  Refresh as RefreshIcon,
  Email as EmailIcon,
  Send as SendIcon,
  Visibility as ViewIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../../services/apiService';
import { useAuth } from '../../../contexts/AuthContext';
import ExternalStorageCard from '../../../components/ExternalStorageCard';
import ExternalStorageDialog from '../../../components/ExternalStorageDialog';
import ConfirmDialog from '../../../components/ConfirmDialog';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const ProjectManagementView = ({ projects, onReload, isAdmin, title }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedProject, setSelectedProject] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [observationCount, setObservationCount] = useState(0);
  const [checkingObservations, setCheckingObservations] = useState(false);
  const [confirmCascadeDelete, setConfirmCascadeDelete] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [editValues, setEditValues] = useState({
    name: '',
    code: '',
    description: '',
    type: 'research',
    status: 'active',
    owner_type: 'user',
    owner_id: null
  });
  // Sorting and pagination state
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  // Subscription project-cap block — prominent centered modal (not inline) when max_projects is
  // met. Holds { message, subject_type, subject_name } so it can name the inherited-from parent.
  const [capLimit, setCapLimit] = useState(null);
  const [newProject, setNewProject] = useState({
    name: '',
    code: '',
    description: '',
    type: 'research',
    owner_type: 'user',
    owner_id: null
  });

  // Edit dialog tab & external storage state
  const [editTabValue, setEditTabValue] = useState(0);
  const [projStorageConfigs, setProjStorageConfigs] = useState([]);
  const [projStorageDialogOpen, setProjStorageDialogOpen] = useState(false);
  const [editingStorageConfig, setEditingStorageConfig] = useState(null);
  const [projStorageLoading, setProjStorageLoading] = useState(false);
  const [projStorageError, setProjStorageError] = useState(null);
  const [testingStorageConfigId, setTestingStorageConfigId] = useState(null);
  const [confirm, setConfirm] = useState(null);
  // Organization storage destinations this user can derive a project config from (#1021).
  const [orgStorageProviders, setOrgStorageProviders] = useState([]);
  useEffect(() => {
    apiService.getOrgStorageProviders()
      .then(setOrgStorageProviders)
      .catch(() => setOrgStorageProviders([]));
  }, []);

  // Email dialog state
  const [openEmailDialog, setOpenEmailDialog] = useState(false);
  const [emailProject, setEmailProject] = useState(null);
  const [emailMembers, setEmailMembers] = useState([]);
  const [emailSelectedIds, setEmailSelectedIds] = useState([]);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailCc, setEmailCc] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailLoadingMembers, setEmailLoadingMembers] = useState(false);

  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        const response = await apiService.getUserOrganizations();
        if (response.data && response.data.organizations) {
          // API returns { organization: {...}, permissions, is_owner } — flatten to just the org objects
          const orgs = response.data.organizations
            .map(m => m.organization)
            .filter(Boolean);
          setOrganizations(orgs);
        }
      } catch (err) {
        console.error('Failed to load organizations:', err);
      }
    };
    loadOrganizations();
  }, []);

  const handleOpenCreateDialog = async () => {
    // Pre-check the personal project cap before opening the dialog: if the operator tier's
    // max_projects is already met, show the prominent modal instead of letting them fill it out.
    // (Org-owned project caps depend on the org chosen in the dialog, so those stay enforced on
    // POST — see handleCreateProject.) Fails open on error; POST is the backstop.
    try {
      const capRes = await apiService.getMyProjectCap();
      if (capRes?.data?.block) {
        setCapLimit(capRes.data);
        return;
      }
    } catch (err) {
      console.error('[Projects] project-cap pre-check failed (allowing):', err);
    }

    setNewProject({
      name: '',
      code: '',
      description: '',
      type: 'research',
      owner_type: 'user',
      owner_id: null
    });
    setCreateError('');
    setCreateDialogOpen(true);
  };


  const handleCreateProject = async () => {
    setCreating(true);
    setCreateError('');
    try {
      const createData = { ...newProject };
      if (!createData.owner_id) {
        delete createData.owner_id;
        createData.owner_type = 'user';
      }
      await apiService.createProject(createData);
      setCreateDialogOpen(false);
      onReload();
    } catch (error) {
      const errData = error.response?.data?.error;
      // Backup for the pre-check (and the only gate for org-owned projects, whose cap depends on
      // the org chosen here): route a subscription cap rejection to the prominent modal.
      if (error.response?.status === 403 && errData?.code === 'SUBSCRIPTION_CAP_EXCEEDED') {
        setCreateDialogOpen(false);
        setCapLimit(errData);
      } else {
        setCreateError((typeof errData === 'object' ? errData?.message : errData) || error.message);
      }
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = (project) => {
    setEditValues({
      name: project.name || '',
      code: project.code || '',
      description: project.description || '',
      type: project.type || 'research',
      status: project.status || 'active',
      owner_type: project.owner_type || 'user',
      owner_id: project.owner_type === 'organization' ? (project.owner_id?._id || project.owner_id) : null
    });
    setEditTabValue(0);
    setProjStorageConfigs([]);
    setProjStorageError(null);
    setSelectedProject(project);
    setEditDialogOpen(true);
    loadProjStorageConfigs(project._id);
  };

  const handleDelete = async (project) => {
    setSelectedProject(project);
    setCheckingObservations(true);
    setConfirmCascadeDelete(false);
    setDeleteDialogOpen(true);

    // Fetch observation count for this project
    try {
      const response = await apiService.getObservations({ project_id: project._id });
      const count = response.data?.observations?.length || 0;
      setObservationCount(count);
    } catch (error) {
      console.error('Failed to fetch observation count:', error);
      setObservationCount(0);
    } finally {
      setCheckingObservations(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      const updateData = { ...editValues };
      if (!updateData.owner_id) {
        delete updateData.owner_id;
        updateData.owner_type = 'user';
      }
      await apiService.updateProject(selectedProject._id, updateData);
      setEditDialogOpen(false);
      setSelectedProject(null);
      onReload();
    } catch (error) {
      console.error('Failed to update project:', error);
    }
  };

  const handleConfirmDelete = async () => {
    // If there are observations, require checkbox confirmation
    if (observationCount > 0 && !confirmCascadeDelete) {
      return; // Don't proceed without confirmation
    }

    try {
      const forceDelete = observationCount > 0;
      await apiService.deleteProject(selectedProject._id, forceDelete);
      setDeleteDialogOpen(false);
      setSelectedProject(null);
      setObservationCount(0);
      setConfirmCascadeDelete(false);
      onReload();
    } catch (error) {
      console.error('Failed to delete project:', error);
      alert(`Failed to delete project: ${error.response?.data?.error?.message || error.message}`);
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedProject(null);
    setObservationCount(0);
    setConfirmCascadeDelete(false);
  };

  const handleManageMembers = (project) => {
    navigate(`/projects/${project._id}/members`);
  };

  const handleArchiveProject = async (project) => {
    try {
      await apiService.updateProject(project._id, {
        ...project,
        status: project.status === 'archived' ? 'active' : 'archived'
      });
      onReload();
    } catch (error) {
      console.error('Failed to archive project:', error);
    }
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedProject(null);
    setEditTabValue(0);
    setProjStorageConfigs([]);
    setProjStorageError(null);
  };

  // Email handlers
  const handleOpenEmailDialog = async (project) => {
    setEmailProject(project);
    setEmailSubject('');
    setEmailMessage('');
    setEmailCc('');
    setEmailSelectedIds([]);
    setEmailMembers([]);
    setOpenEmailDialog(true);
    setEmailLoadingMembers(true);

    try {
      const response = await apiService.getProjectMembers(project._id);
      const membersList = response.data?.members || [];
      setEmailMembers(membersList);
      setEmailSelectedIds(membersList.map(m => m.user_id?._id || m.user_id));
    } catch (err) {
      console.error('Error loading members for email:', err);
      alert('Failed to load project members');
    } finally {
      setEmailLoadingMembers(false);
    }
  };

  const handleCloseEmailDialog = () => {
    setOpenEmailDialog(false);
    setEmailProject(null);
    setEmailMembers([]);
    setEmailSelectedIds([]);
    setEmailSubject('');
    setEmailMessage('');
    setEmailCc('');
  };

  const handleToggleEmailMember = (memberId) => {
    setEmailSelectedIds(prev =>
      prev.includes(memberId)
        ? prev.filter(id => id !== memberId)
        : [...prev, memberId]
    );
  };

  const handleToggleAllEmailMembers = () => {
    if (emailSelectedIds.length === emailMembers.length) {
      setEmailSelectedIds([]);
    } else {
      setEmailSelectedIds(emailMembers.map(m => m.user_id?._id || m.user_id));
    }
  };

  const handleSendEmail = async () => {
    if (!emailSubject.trim()) {
      alert('Subject is required');
      return;
    }
    if (!emailMessage.trim()) {
      alert('Message is required');
      return;
    }
    if (emailSelectedIds.length === 0) {
      alert('Please select at least one recipient');
      return;
    }

    setEmailSending(true);
    try {
      const recipientEmails = emailMembers
        .filter(m => emailSelectedIds.includes(m.user_id?._id || m.user_id))
        .map(m => m.user_id?.email)
        .filter(Boolean);

      const ccEmails = emailCc
        .split(/[,;\s]+/)
        .map(e => e.trim())
        .filter(e => e && e.includes('@'));
      const allRecipients = [...recipientEmails, ...ccEmails];

      await apiService.sendEmail({
        to: allRecipients,
        subject: emailSubject.trim(),
        message: emailMessage.trim(),
        replyTo: user?.email,
      });
      alert(`Email sent to ${recipientEmails.length} recipient(s)`);
      handleCloseEmailDialog();
    } catch (err) {
      console.error('Error sending email:', err);
      alert(err.response?.data?.error?.message || 'Failed to send email');
    } finally {
      setEmailSending(false);
    }
  };

  // Project External Storage handlers
  const loadProjStorageConfigs = async (projectId) => {
    try {
      const response = await apiService.getProjectStorageConfigs(projectId);
      if (response?.success) {
        setProjStorageConfigs(response.data?.configs || []);
      }
    } catch (err) {
      console.error('Failed to load project storage configs:', err);
    }
  };

  const handleProjStorageSave = async (data, configId) => {
    if (!selectedProject) return;
    setProjStorageLoading(true);
    setProjStorageError(null);
    try {
      const payload = {
        ...data,
        owner_type: 'project',
        owner_id: selectedProject._id,
      };
      if (configId) {
        await apiService.updateExternalStorageConfig(configId, payload);
      } else {
        await apiService.createExternalStorageConfig(payload);
      }
      await loadProjStorageConfigs(selectedProject._id);
      setProjStorageDialogOpen(false);
      setEditingStorageConfig(null);
    } catch (err) {
      const msg = err.response?.data?.error?.message || err.message || 'Failed to save storage configuration';
      setProjStorageError(msg);
    } finally {
      setProjStorageLoading(false);
    }
  };

  const handleProjStorageDelete = (config) => {
    setConfirm({
      title: 'Delete Storage Configuration',
      message: (
        <span>Delete storage configuration <strong>{config.display_name}</strong>?</span>
      ),
      confirmLabel: 'Delete',
      confirmColor: 'error',
      confirmIcon: <DeleteIcon />,
      icon: <DeleteIcon color="error" />,
      onConfirm: async () => {
        try {
          await apiService.deleteExternalStorageConfig(config._id);
          setProjStorageConfigs(prev => prev.filter(c => c._id !== config._id));
        } catch (err) {
          console.error('Failed to delete storage configuration:', err);
          throw err;
        }
      },
    });
  };

  const handleProjStorageTest = async (configId) => {
    setTestingStorageConfigId(configId);
    try {
      const result = await apiService.testExternalStorageConfig(configId);
      if (!result?.success) {
        console.error(result?.error?.message || 'Connection test failed');
      }
    } catch (err) {
      console.error(err.response?.data?.error?.message || 'Connection test failed');
    } finally {
      // Always reload so the card shows the persisted last-test result, whether
      // the test passed or failed (a failed test throws, so reloading only in
      // the try body left the card stale — looked like nothing happened).
      if (selectedProject) {
        await loadProjStorageConfigs(selectedProject._id).catch(() => {});
      }
      setTestingStorageConfigId(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'paused':
        return 'warning';
      case 'archived':
        return 'default';
      case 'completed':
        return 'info';
      default:
        return 'default';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'research':
        return 'primary';
      case 'education':
        return 'secondary';
      case 'outreach':
        return 'info';
      case 'personal':
        return 'success';
      case 'testing':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Get user's access level chip for display
  const getAccessLevelChip = (project) => {
    const perms = project.user_permissions;

    if (project.is_owner) {
      return <Chip icon={<OwnerIcon />} label="Owner" size="small" color="error" />;
    }
    if (!perms) {
      return <Chip label="View" size="small" color="default" />;
    }
    if (perms.can_delete) {
      return <Chip label="Full Access" size="small" color="error" />;
    }
    if (perms.can_manage_members) {
      return <Chip label="Manager" size="small" color="warning" />;
    }
    if (perms.can_edit) {
      return <Chip label="Editor" size="small" color="primary" />;
    }
    if (perms.can_use) {
      return <Chip label="Use" size="small" color="success" />;
    }
    return <Chip label="View" size="small" color="default" />;
  };

  // Check if user can perform actions on this project
  const canEditProject = (project) => {
    return isAdmin || project.is_owner || project.user_permissions?.can_edit;
  };

  const canManageProjectMembers = (project) => {
    return isAdmin || project.is_owner || project.user_permissions?.can_manage_members;
  };

  const canDeleteProject = (project) => {
    return isAdmin || project.is_owner || project.user_permissions?.can_delete;
  };

  const canManageProjectStorage = (project) => {
    return isAdmin || project.is_owner || project.user_permissions?.can_manage_storage;
  };

  const handleSort = (field) => {
    const newOrder = sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortBy(field);
    setSortOrder(newOrder);
  };

  const sortedProjects = React.useMemo(() => {
    const sorted = [...projects];
    const dir = sortOrder === 'asc' ? 1 : -1;
    sorted.sort((a, b) => {
      let valA, valB;
      switch (sortBy) {
        case 'name':
          valA = (a.name || '').toLowerCase();
          valB = (b.name || '').toLowerCase();
          return dir * valA.localeCompare(valB);
        case 'code':
          valA = (a.code || '').toLowerCase();
          valB = (b.code || '').toLowerCase();
          return dir * valA.localeCompare(valB);
        case 'type':
          valA = a.type || '';
          valB = b.type || '';
          return dir * valA.localeCompare(valB);
        case 'status':
          valA = a.status || 'active';
          valB = b.status || 'active';
          return dir * valA.localeCompare(valB);
        case 'owner':
          valA = (a.owner_id?.name || a.owner_id?.display_name || a.owner_id?.email || '').toLowerCase();
          valB = (b.owner_id?.name || b.owner_id?.display_name || b.owner_id?.email || '').toLowerCase();
          return dir * valA.localeCompare(valB);
        case 'members':
          return dir * ((a.member_count || 0) - (b.member_count || 0));
        case 'observations':
          return dir * ((a.stats?.total || 0) - (b.stats?.total || 0));
        case 'created':
          return dir * (new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime());
        default:
          return 0;
      }
    });
    return sorted;
  }, [projects, sortBy, sortOrder]);

  const paginatedProjects = sortedProjects.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const isCreateFormValid = newProject.name.trim() && newProject.code.trim() && newProject.description.trim();

  const renderCreateDialog = () => (
    <>
    <Dialog
      open={createDialogOpen}
      onClose={() => !creating && setCreateDialogOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Create Project</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 2 }}>
          {createError && (
            <Alert severity="error">{createError}</Alert>
          )}
          <TextField
            label="Project Name"
            value={newProject.name}
            onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
            fullWidth
            required
          />
          <TextField
            label="Project Code"
            value={newProject.code}
            onChange={(e) => setNewProject({ ...newProject, code: e.target.value })}
            fullWidth
            required
            helperText="Short unique identifier (e.g. PROJ-001)"
          />
          <TextField
            label="Description"
            value={newProject.description}
            onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
            multiline
            rows={3}
            fullWidth
            required
          />
          <FormControl fullWidth>
            <InputLabel>Type</InputLabel>
            <Select
              value={newProject.type}
              onChange={(e) => setNewProject({ ...newProject, type: e.target.value })}
              label="Type"
            >
              <MenuItem value="research">Research</MenuItem>
              <MenuItem value="education">Education</MenuItem>
              <MenuItem value="outreach">Outreach</MenuItem>
              <MenuItem value="personal">Personal</MenuItem>
              <MenuItem value="testing">Testing</MenuItem>
            </Select>
          </FormControl>
          {organizations.length > 0 && (
            <FormControl fullWidth>
              <InputLabel>Organization (Optional)</InputLabel>
              <Select
                value={newProject.owner_id || ''}
                onChange={(e) => {
                  const orgId = e.target.value;
                  if (orgId) {
                    setNewProject({ ...newProject, owner_type: 'organization', owner_id: orgId });
                  } else {
                    setNewProject({ ...newProject, owner_type: 'user', owner_id: null });
                  }
                }}
                label="Organization (Optional)"
              >
                <MenuItem value="">Personal (no organization)</MenuItem>
                {organizations.map((org) => (
                  <MenuItem key={org._id} value={org._id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Assign this project to an organization</FormHelperText>
            </FormControl>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setCreateDialogOpen(false)}
          disabled={creating}
        >
          Cancel
        </Button>
        <Button
          onClick={handleCreateProject}
          variant="contained"
          disabled={creating || !isCreateFormValid}
        >
          {creating ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          {creating ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Subscription project-cap block — prominent centered modal that halts project creation */}
    <Dialog open={Boolean(capLimit)} onClose={() => setCapLimit(null)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
        <WarningIcon color="error" />
        Subscription Limit Reached
      </DialogTitle>
      <DialogContent>
        <Alert severity="error" variant="outlined" sx={{ mt: 1 }}>
          {capLimit?.message}
        </Alert>
        {capLimit?.subject_name && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Your subscription limits are inherited from the {capLimit.subject_type || 'organization'}{' '}
            <strong>{capLimit.subject_name}</strong>.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCapLimit(null)} variant="contained" autoFocus>OK</Button>
      </DialogActions>
    </Dialog>
    </>
  );

  if (projects.length === 0) {
    return (
      <>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {isAdmin
              ? 'No projects found.  Click "Add Project" to create one.'
              : 'You are not a member of any projects yet.'}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
            sx={{ mt: 2 }}
          >
            New Project
          </Button>
        </Paper>

        {/* Create Dialog (rendered in empty state) */}
        {renderCreateDialog()}
      </>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        {title && <Typography variant="h4">{title}</Typography>}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={onReload}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            New Project
          </Button>
        </Box>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel active={sortBy === 'name'} direction={sortBy === 'name' ? sortOrder : 'asc'} onClick={() => handleSort('name')}>
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortBy === 'code'} direction={sortBy === 'code' ? sortOrder : 'asc'} onClick={() => handleSort('code')}>
                  Code
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortBy === 'type'} direction={sortBy === 'type' ? sortOrder : 'asc'} onClick={() => handleSort('type')}>
                  Type
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortBy === 'status'} direction={sortBy === 'status' ? sortOrder : 'asc'} onClick={() => handleSort('status')}>
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>Your Access</TableCell>
              <TableCell>
                <TableSortLabel active={sortBy === 'owner'} direction={sortBy === 'owner' ? sortOrder : 'asc'} onClick={() => handleSort('owner')}>
                  Owner
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortBy === 'members'} direction={sortBy === 'members' ? sortOrder : 'asc'} onClick={() => handleSort('members')}>
                  Members
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortBy === 'observations'} direction={sortBy === 'observations' ? sortOrder : 'asc'} onClick={() => handleSort('observations')}>
                  Observations
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel active={sortBy === 'created'} direction={sortBy === 'created' ? sortOrder : 'asc'} onClick={() => handleSort('created')}>
                  Created
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedProjects.map((project) => (
              <TableRow key={project._id}>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {project.name}
                  </Typography>
                  {project.description && (
                    <Typography variant="caption" color="text.secondary">
                      {project.description.substring(0, 50)}
                      {project.description.length > 50 ? '...' : ''}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontFamily="monospace">
                    {project.code}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    label={project.type} 
                    size="small"
                    color={getTypeColor(project.type)}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={project.status || 'active'}
                    size="small"
                    color={getStatusColor(project.status || 'active')}
                  />
                </TableCell>
                <TableCell>
                  {getAccessLevelChip(project)}
                </TableCell>
                <TableCell>
                  {project.owner_type === 'organization' ? (
                    <Box>
                      <Chip label="Org" size="small" color="info" variant="outlined" sx={{ mb: 0.5 }} />
                      <Typography variant="body2">
                        {project.owner_id?.name || organizations.find(o => o._id === (project.owner_id?._id || project.owner_id))?.name || 'Organization'}
                      </Typography>
                    </Box>
                  ) : project.owner_id ? (
                    <Box>
                      <Typography variant="body2">
                        {project.owner_id.first_name} {project.owner_id.last_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {project.owner_id.email}
                      </Typography>
                    </Box>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <GroupIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {project.member_count || 0}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell>
                  {project.stats ? (
                    <Stack spacing={0.5}>
                      <Typography variant="body2">
                        Total: {project.stats.total}
                      </Typography>
                      {project.stats.running > 0 && (
                        <Chip 
                          label={`${project.stats.running} running`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                      {project.stats.pending > 0 && (
                        <Chip 
                          label={`${project.stats.pending} pending`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  ) : (
                    <Typography variant="body2">
                      {project.stats?.total ?? project.observation_count ?? 0}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {project.created_at 
                    ? format(parseISO(project.created_at), 'MMM dd, yyyy')
                    : '-'}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => navigate(`/projects/${project._id}/members`)}>
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {canEditProject(project) && (
                    <Tooltip title={project.status === 'archived' ? 'Unarchive Project' : 'Archive Project'}>
                      <IconButton size="small" onClick={() => handleArchiveProject(project)}>
                        <ArchiveIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canManageProjectMembers(project) && (
                    <Tooltip title="Manage Members">
                      <IconButton size="small" color="secondary" onClick={() => handleManageMembers(project)}>
                        <GroupIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Project History">
                    <IconButton size="small" onClick={() => navigate(`/projects/${project._id}/history`)}>
                      <HistoryIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {canManageProjectMembers(project) && (
                    <Tooltip title="Email Members">
                      <IconButton size="small" color="info" onClick={() => handleOpenEmailDialog(project)}>
                        <EmailIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canEditProject(project) && (
                    <Tooltip title="Edit Project">
                      <IconButton size="small" onClick={() => handleEdit(project)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDeleteProject(project) && (
                    <Tooltip title="Delete Project">
                      <IconButton size="small" color="error" onClick={() => handleDelete(project)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={sortedProjects.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Project</DialogTitle>
        <DialogContent>
          <Tabs value={editTabValue} onChange={(e, v) => setEditTabValue(v)} sx={{ mb: 0 }}>
            <Tab label="Basic Info" />
            <Tab label="External Storage" icon={<CloudUploadIcon />} iconPosition="start" />
          </Tabs>

          <TabPanel value={editTabValue} index={0}>
            <Stack spacing={2}>
              <TextField
                label="Project Name"
                value={editValues.name}
                onChange={(e) => setEditValues({ ...editValues, name: e.target.value })}
                fullWidth
              />
              <TextField
                label="Project Code"
                value={editValues.code}
                onChange={(e) => setEditValues({ ...editValues, code: e.target.value })}
                fullWidth
              />
              <TextField
                label="Description"
                value={editValues.description}
                onChange={(e) => setEditValues({ ...editValues, description: e.target.value })}
                multiline
                rows={3}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={editValues.type}
                  onChange={(e) => setEditValues({ ...editValues, type: e.target.value })}
                  label="Type"
                >
                  <MenuItem value="research">Research</MenuItem>
                  <MenuItem value="education">Education</MenuItem>
                  <MenuItem value="outreach">Outreach</MenuItem>
                  <MenuItem value="personal">Personal</MenuItem>
                  <MenuItem value="testing">Testing</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={editValues.status}
                  onChange={(e) => setEditValues({ ...editValues, status: e.target.value })}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="paused">Paused</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
              </FormControl>
              {organizations.length > 0 && (
                <FormControl fullWidth>
                  <InputLabel>Organization (Optional)</InputLabel>
                  <Select
                    value={editValues.owner_id || ''}
                    onChange={(e) => {
                      const orgId = e.target.value;
                      if (orgId) {
                        setEditValues({ ...editValues, owner_type: 'organization', owner_id: orgId });
                      } else {
                        setEditValues({ ...editValues, owner_type: 'user', owner_id: null });
                      }
                    }}
                    label="Organization (Optional)"
                  >
                    <MenuItem value="">Personal (no organization)</MenuItem>
                    {organizations.map((org) => (
                      <MenuItem key={org._id} value={org._id}>
                        {org.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Assign this project to an organization</FormHelperText>
                </FormControl>
              )}
            </Stack>
          </TabPanel>

          <TabPanel value={editTabValue} index={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1">
                Storage destinations for this project
              </Typography>
              {selectedProject && canManageProjectStorage(selectedProject) && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => { setEditingStorageConfig(null); setProjStorageError(null); setProjStorageDialogOpen(true); }}
                >
                  Add Storage
                </Button>
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />
            {projStorageConfigs.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No external storage destinations configured for this project. Add one to automatically copy FITS files to external storage.
              </Typography>
            ) : (
              projStorageConfigs.map(config => (
                <ExternalStorageCard
                  key={config._id}
                  config={config}
                  onEdit={selectedProject && canManageProjectStorage(selectedProject) ? (c) => { setEditingStorageConfig(c); setProjStorageError(null); setProjStorageDialogOpen(true); } : null}
                  onDelete={selectedProject && canManageProjectStorage(selectedProject) ? handleProjStorageDelete : null}
                  onTest={selectedProject && canManageProjectStorage(selectedProject) ? handleProjStorageTest : null}
                  testing={testingStorageConfigId === config._id}
                />
              ))
            )}
            {selectedProject && canManageProjectStorage(selectedProject) && (
              <ExternalStorageDialog
                open={projStorageDialogOpen}
                onClose={() => { setProjStorageDialogOpen(false); setEditingStorageConfig(null); setProjStorageError(null); }}
                onSave={handleProjStorageSave}
                config={editingStorageConfig}
                loading={projStorageLoading}
                error={projStorageError}
                orgProviders={orgStorageProviders}
              />
            )}
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>
            Cancel
          </Button>
          <Button onClick={handleSaveEdit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="error" />
            Delete Project
          </Box>
        </DialogTitle>
        <DialogContent>
          {checkingObservations ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
              <CircularProgress size={24} />
              <Typography>Checking for associated observations...</Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              <Typography>
                Are you sure you want to delete <strong>"{selectedProject?.name}"</strong>?
              </Typography>

              {observationCount > 0 ? (
                <>
                  <Alert severity="error" icon={<WarningIcon />}>
                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                      This project has {observationCount} associated observation{observationCount !== 1 ? 's' : ''}
                    </Typography>
                    <Typography variant="body2">
                      Deleting this project will permanently delete all {observationCount} observation{observationCount !== 1 ? 's' : ''} and their associated data including FITS files, metadata, and execution history.
                    </Typography>
                  </Alert>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={confirmCascadeDelete}
                        onChange={(e) => setConfirmCascadeDelete(e.target.checked)}
                        color="error"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        I understand that this will permanently delete {observationCount} observation{observationCount !== 1 ? 's' : ''} and cannot be undone
                      </Typography>
                    }
                  />
                </>
              ) : (
                <Alert severity="warning">
                  <Typography variant="body2">
                    This action cannot be undone.
                  </Typography>
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={checkingObservations || (observationCount > 0 && !confirmCascadeDelete)}
          >
            {observationCount > 0 ? `Delete Project & ${observationCount} Observation${observationCount !== 1 ? 's' : ''}` : 'Delete Project'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Dialog */}
      {renderCreateDialog()}

      {/* Email Members Dialog */}
      <Dialog open={openEmailDialog} onClose={handleCloseEmailDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon />
            Email Members - {emailProject?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              From: Asterism (replies go to {user?.email})
            </Typography>
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Recipients</Typography>
          {emailLoadingMembers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Box sx={{ mb: 2, maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={emailSelectedIds.length === emailMembers.length && emailMembers.length > 0}
                    indeterminate={emailSelectedIds.length > 0 && emailSelectedIds.length < emailMembers.length}
                    onChange={handleToggleAllEmailMembers}
                  />
                }
                label={<Typography variant="body2" fontWeight="bold">Select All</Typography>}
              />
              <Divider sx={{ my: 0.5 }} />
              {emailMembers.map((member) => {
                const memberId = member.user_id?._id || member.user_id;
                const memberName = member.user_id?.display_name || member.user_id?.email || 'Unknown';
                const memberEmail = member.user_id?.email || '';
                return (
                  <FormControlLabel
                    key={memberId}
                    control={
                      <Checkbox
                        checked={emailSelectedIds.includes(memberId)}
                        onChange={() => handleToggleEmailMember(memberId)}
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {memberName} {memberEmail && `(${memberEmail})`}
                      </Typography>
                    }
                    sx={{ display: 'flex', width: '100%' }}
                  />
                );
              })}
              {emailMembers.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  No members found
                </Typography>
              )}
            </Box>
          )}

          <TextField
            label="CC"
            value={emailCc}
            onChange={(e) => setEmailCc(e.target.value)}
            fullWidth
            placeholder="Additional email addresses (comma separated)"
            helperText="Add external recipients not in the system"
            sx={{ mb: 2 }}
          />

          <TextField
            label="Subject"
            value={emailSubject}
            onChange={(e) => setEmailSubject(e.target.value)}
            fullWidth
            required
            sx={{ mb: 2 }}
          />

          <TextField
            label="Message"
            value={emailMessage}
            onChange={(e) => setEmailMessage(e.target.value)}
            fullWidth
            required
            multiline
            rows={6}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEmailDialog} disabled={emailSending}>Cancel</Button>
          <Button
            onClick={handleSendEmail}
            variant="contained"
            startIcon={emailSending ? <CircularProgress size={16} /> : <SendIcon />}
            disabled={emailSending || emailSelectedIds.length === 0 || !emailSubject.trim() || !emailMessage.trim()}
          >
            {emailSending ? 'Sending...' : `Send (${emailSelectedIds.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        confirmColor={confirm?.confirmColor}
        confirmIcon={confirm?.confirmIcon}
        icon={confirm?.icon}
        onConfirm={confirm?.onConfirm}
        onClose={() => setConfirm(null)}
      />
    </Box>
  );
};

export default ProjectManagementView;